import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import IconFont from '$/components/IconFont';
import { PopupPosition, type PopupInfoResponse } from '$/api/server.types';
import { replaceTemplateWithElements, useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { openVipModal } from '$/store/useVipModalStore';
import { mtstatTrack } from '$/utils/mtstat';
import { useCommonStore } from '@/store/useCommonStore';

import styles from './index.module.scss';

const HOME_SUBSCRIBE_ACTIVITY_BANNER_HEIGHT = 48;
const HOME_SALES_FROM_SOURCE = 'homepage';
const SUBSCRIPTION_SALES_FROM_SOURCE = 'subscription_page';
const SECOND_MS = 1000;

type PopupItem = PopupInfoResponse['popup'][number];
type TrackParamValue = string | number | boolean | null | undefined;
type HomeSalesTrackParams = Record<string, string>;

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface BannerVisualImages {
  backgroundImageUrl?: string;
  foregroundImageUrl?: string;
}

/**
 * 将接口秒级时间戳统一转为毫秒，兼容未来误传毫秒级时间戳的情况。
 */
function normalizeTimestampMs(timestamp?: number) {
  if (!timestamp) return 0;
  return timestamp > 1e12 ? timestamp : timestamp * SECOND_MS;
}

/**
 * 根据剩余毫秒数拆分顶部活动倒计时展示字段。
 */
function getCountdownTime(leftMs: number): CountdownTime {
  const totalSeconds = Math.max(Math.floor(leftMs / SECOND_MS), 0);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    days,
    hours,
    minutes,
    seconds,
  };
}

/**
 * 将倒计时小时、分钟、秒补齐为两位，贴合设计稿展示。
 */
function formatCountdownPart(value: number) {
  return `${value}`.padStart(2, '0');
}

/**
 * 解析首页营销位的分层素材，并为历史单图配置保留 ext.img 背景兜底。
 */
function getBannerVisualImages(bannerInfo?: PopupItem | null): BannerVisualImages {
  return {
    backgroundImageUrl: bannerInfo?.ext?.background?.img?.url ?? bannerInfo?.ext?.img?.url,
    foregroundImageUrl: bannerInfo?.ext?.foreground?.img?.url,
  };
}

/**
 * 从 common/config 的 popup 列表中取首页顶部活动 banner 配置。
 */
function getHomeTopBanner(data: PopupInfoResponse) {
  return data.popup.find((item) => {
    const { backgroundImageUrl, foregroundImageUrl } = getBannerVisualImages(item);
    return item.position === PopupPosition.HOME_TOP_BANNER
      && Boolean(backgroundImageUrl || foregroundImageUrl);
  });
}

/**
 * 预加载营销位单张素材，供背景图和前景图统一等待后再展示。
 */
function preloadBannerImage(url: string) {
  return new Promise<void>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve();
    image.onerror = () => reject(new Error(`Failed to load home top banner image: ${url}`));
    image.src = url;
    if (image.complete && image.naturalWidth > 0) resolve();
  });
}

/**
 * 过滤未下发或未获取到的埋点字段，满足「字段有则上报，没有就不上报」。
 */
function compactTrackParams(params: Record<string, TrackParamValue>): HomeSalesTrackParams {
  const result: HomeSalesTrackParams = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    result[key] = String(value);
  });
  return result;
}

/**
 * 根据当前页面路径识别营销条来源，默认按首页顶部活动条上报。
 */
function getHomeSalesFromSource() {
  if (typeof window === 'undefined') return HOME_SALES_FROM_SOURCE;

  const pathname = window.location.pathname.toLowerCase();
  if (pathname.includes('subscription') || pathname.includes('vip')) {
    return SUBSCRIPTION_SALES_FROM_SOURCE;
  }

  return HOME_SALES_FROM_SOURCE;
}

/**
 * 获取 popup 配置中的内容 id，兼容后端未下发 id 的活动配置。
 */
function getBannerContentId(bannerInfo?: PopupItem | null) {
  const contentId = bannerInfo?.id;
  return contentId === undefined || contentId === null || contentId === '' ? undefined : contentId;
}

/**
 * 生成活动条曝光和点击共用的基础埋点参数。
 */
function buildBaseTrackParams(bannerInfo?: PopupItem | null) {
  return compactTrackParams({
    content_id: getBannerContentId(bannerInfo),
    from_source: getHomeSalesFromSource(),
  });
}

/**
 * 将当前会员态转换为点击埋点参数，未登录按非会员，登录后订阅信息缺失才按获取失败上报。
 */
function buildMemberTrackParams(
  subscribe: ReturnType<typeof useOfficialStore.getState>['userInfo']['subscribe'],
  isUserLoggedIn: boolean,
) {
  if (!isUserLoggedIn) {
    return compactTrackParams({
      member_status: 'non_member',
    });
  }

  if (!subscribe) {
    return compactTrackParams({
      member_status: 'fetch_failed',
    });
  }

  if (!subscribe.isValid) {
    return compactTrackParams({
      member_status: 'non_member',
    });
  }

  return compactTrackParams({
    member_status: 'vip',
    level: subscribe.level,
    invalid_time: subscribe.invalidTime,
  });
}

/**
 * 为同一条活动配置生成稳定曝光去重 key，缺少 id 时退回图片地址。
 */
function getBannerTrackKey(bannerInfo: PopupItem) {
  const { backgroundImageUrl, foregroundImageUrl } = getBannerVisualImages(bannerInfo);
  return String(getBannerContentId(bannerInfo) ?? foregroundImageUrl ?? backgroundImageUrl);
}

/**
 * 渲染订阅活动顶部 banner：接口背景图、前景图、倒计时和关闭按钮，并同步首页顶部布局占位。
 */
export default function HomeSubscribeActivityBanner() {
  const bannerRef = useRef<HTMLElement>(null);
  const countdownOffsetRef = useRef(0);
  const reportedShowKeyRef = useRef<string | null>(null);
  const loginPendingRef = useRef(false);
  const languageStore = useLanguageStore((state) => state.languageStore);
  const popupInfo = useCommonStore((state) => state.popupInfo);
  const toLogin = useCommonStore((state) => state.toLogin);
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const userSubscribe = useOfficialStore((state) => state.userInfo.subscribe);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const [bannerInfo, setBannerInfo] = useState<PopupItem | null>(null);
  const [visible, setVisible] = useState(false);
  const [visualReady, setVisualReady] = useState(false);
  const [leftMs, setLeftMs] = useState(0);
  // 按当前首页营销位的独立配置决定是否展示倒计时。
  const showCountdown = bannerInfo?.ext?.show_countdown === true;
  const {
    backgroundImageUrl,
    foregroundImageUrl,
  } = useMemo(() => getBannerVisualImages(bannerInfo), [bannerInfo]);

  const countdown = useMemo(() => getCountdownTime(leftMs), [leftMs]);
  // 倒计时数值和单位统一交给营销活动专用模板，确保各语言可自行控制顺序与间距。
  const countdownText = useMemo(() => ({
    days: replaceTemplateWithElements('promo_countdown_days', {
      time: String(countdown.days),
    }),
    hours: replaceTemplateWithElements('promo_countdown_hours', {
      time: formatCountdownPart(countdown.hours),
    }),
    minutes: replaceTemplateWithElements('promo_countdown_minutes', {
      time: formatCountdownPart(countdown.minutes),
    }),
    seconds: replaceTemplateWithElements('promo_countdown_seconds', {
      time: formatCountdownPart(countdown.seconds),
    }),
  }), [countdown.days, countdown.hours, countdown.minutes, countdown.seconds, languageStore]);

  useEffect(() => {
    let cancelled = false;

    if (!popupInfo) {
      setVisible(false);
      setVisualReady(false);
      setBannerInfo(null);
      return undefined;
    }

    const nextBannerInfo = getHomeTopBanner(popupInfo);
    if (!nextBannerInfo) {
      setVisible(false);
      setVisualReady(false);
      setBannerInfo(null);
      return undefined;
    }

    const nextVisualImages = getBannerVisualImages(nextBannerInfo);
    const visualImageUrls = [
      nextVisualImages.backgroundImageUrl,
      nextVisualImages.foregroundImageUrl,
    ].filter(Boolean) as string[];

    const serverTimeMs = normalizeTimestampMs(popupInfo.server_time);
    countdownOffsetRef.current = serverTimeMs ? serverTimeMs - Date.now() : 0;
    setBannerInfo(nextBannerInfo);
    setVisible(false);
    setVisualReady(false);

    // 背景和前景全部加载完成后再整体展示，避免分层素材出现闪烁。
    Promise.all(visualImageUrls.map(preloadBannerImage))
      .then(() => {
        if (cancelled) return;
        setVisualReady(true);
        setVisible(true);
      })
      .catch(() => {
        if (cancelled) return;
        setVisualReady(false);
        setVisible(false);
        setBannerInfo(null);
      });

    return () => {
      cancelled = true;
    };
  }, [popupInfo]);

  useEffect(() => {
    if (!visible) {
      document.documentElement.style.removeProperty('--seedance-guide-height');
      return undefined;
    }

    /**
     * 按真实渲染高度同步首页顶部占位，避免 Header 与侧栏压到活动条。
     */
    const updateBannerHeight = () => {
      const height = bannerRef.current?.offsetHeight || HOME_SUBSCRIBE_ACTIVITY_BANNER_HEIGHT;
      document.documentElement.style.setProperty('--seedance-guide-height', `${height}px`);
    };

    updateBannerHeight();
    window.addEventListener('resize', updateBannerHeight);

    const resizeObserver = typeof ResizeObserver !== 'undefined' && bannerRef.current
      ? new ResizeObserver(updateBannerHeight)
      : null;
    resizeObserver?.observe(bannerRef.current as HTMLElement);

    return () => {
      window.removeEventListener('resize', updateBannerHeight);
      resizeObserver?.disconnect();
      document.documentElement.style.removeProperty('--seedance-guide-height');
    };
  }, [visible]);

  useEffect(() => {
    if (!visible || !showCountdown || !bannerInfo?.end_time) {
      setLeftMs(0);
      return undefined;
    }

    const endTimeMs = normalizeTimestampMs(bannerInfo.end_time);

    /**
     * 使用接口 server_time 修正后的当前时间刷新倒计时，并在结束后自动隐藏。
     */
    const updateCountdown = () => {
      const nowMs = Date.now() + countdownOffsetRef.current;
      const nextLeftMs = Math.max(endTimeMs - nowMs, 0);
      setLeftMs(nextLeftMs);
      if (nextLeftMs <= 0) {
        setVisible(false);
      }
    };

    updateCountdown();
    const timer = window.setInterval(updateCountdown, SECOND_MS);

    return () => {
      window.clearInterval(timer);
    };
  }, [visible, showCountdown, bannerInfo?.end_time]);

  useEffect(() => {
    if (!visible || !visualReady || !bannerInfo) return;

    const trackKey = getBannerTrackKey(bannerInfo);
    if (reportedShowKeyRef.current === trackKey) return;

    reportedShowKeyRef.current = trackKey;
    mtstatTrack('roboneo_homepage_sales_show', buildBaseTrackParams(bannerInfo));
  }, [visible, visualReady, bannerInfo]);

  /**
   * 点击顶部营销 banner 时上报点击埋点；未登录只走登录流程，已登录才打开订阅弹窗。
   */
  const handleBannerClick = useCallback(async () => {
    if (!isUserLoggedIn && loginPendingRef.current) {
      return;
    }

    const baseTrackParams = buildBaseTrackParams(bannerInfo);

    mtstatTrack('roboneo_homepage_sales_click', {
      ...baseTrackParams,
      ...buildMemberTrackParams(userSubscribe, isUserLoggedIn),
    });

    if (!isUserLoggedIn) {
      loginPendingRef.current = true;
      try {
        await toLogin();
        await initUserInfo();
      } catch {
        loginPendingRef.current = false;
        return;
      }

      loginPendingRef.current = false;
      return;
    }

    openVipModal?.('home_banner', undefined, {
      ...baseTrackParams,
      // 顶部营销组件自身销售埋点口径保持不变，仅购买弹窗链路使用营销组件来源。
      from_source: 'marketing_component',
    });
  }, [bannerInfo, initUserInfo, isUserLoggedIn, toLogin, userSubscribe]);

  /**
   * 关闭当前顶部活动 banner，并释放页面顶部占位。
   */
  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  if (!visible || !visualReady || !bannerInfo || (!backgroundImageUrl && !foregroundImageUrl)) {
    return null;
  }

  return (
    <section
      ref={bannerRef}
      className={styles.banner}
      aria-label={bannerInfo.title || '首页顶部活动横幅'}
    >
      <button
        type="button"
        className={styles.bannerButton}
        onClick={handleBannerClick}
        aria-label={bannerInfo.title || '查看首页顶部活动'}
      >
        {backgroundImageUrl ? (
          <img
            className={styles.backgroundImage}
            src={backgroundImageUrl}
            alt=""
            aria-hidden
          />
        ) : null}
        {foregroundImageUrl ? (
          <img
            className={styles.foregroundImage}
            src={foregroundImageUrl}
            alt=""
            aria-hidden
          />
        ) : null}
        {showCountdown && bannerInfo.end_time ? (
          <div className={styles.countdown} aria-label="活动倒计时">
            <span>
              {countdownText.days}
            </span>
            <span className={styles.divider} aria-hidden />
            <span>
              {countdownText.hours}
            </span>
            <span className={styles.separator}>:</span>
            <span>
              {countdownText.minutes}
            </span>
            <span className={styles.separator}>:</span>
            <span>
              {countdownText.seconds}
            </span>
          </div>
        ) : null}
      </button>
      <button
        type="button"
        className={styles.closeButton}
        onClick={handleClose}
        aria-label="关闭横幅"
      >
        <IconFont name="crossBlack" fontSize={16} color="currentColor" aria-hidden />
      </button>
    </section>
  );
}
